// The phone's sheet (D191 A, B3a; docs/design-proposals/2026-09-29-meeting-board-ux.html §3): a panel that rests at one
// of three heights — peek, half, full — and is moved between them by dragging its handle or header, with the snap
// decided by where it was let go and how fast (a flick up from half goes to full; a drag below peek closes it). The
// maths is pure and tested in node; `attachSheet` is the small DOM controller that uses it.
export const DETENTS = ["peek", "half", "full"];
export const FRACTION = { peek: 0.25, half: 0.55 };
export const FLICK = 0.5;   // px/ms: faster than this, the direction of the movement decides, not the position
export const CLOSE_BELOW = 0.6; // let go below this fraction of peek: the sheet closes

// The three heights in px for a viewport `vh` tall with a `top` px bar the sheet never covers.
export function heightsFor(vh, top = 48) {
  const full = Math.max(120, Math.round(vh - top));
  return { peek: Math.round(vh * FRACTION.peek), half: Math.round(vh * FRACTION.half), full };
}

// Where a sheet let go at height `h` (px) moving at `vy` (px/ms, positive = growing) comes to rest: a detent, or
// "closed". `from` is the detent it started from (a flick goes one step from there).
export function snapTo({ h, vy = 0, from = "half", heights }) {
  const order = DETENTS.map((d) => [d, heights[d]]);
  if (h < heights.peek * CLOSE_BELOW && vy <= 0) return "closed";
  if (Math.abs(vy) >= FLICK) {
    const i = DETENTS.indexOf(from);
    if (vy > 0) return DETENTS[Math.min(DETENTS.length - 1, i + 1)];
    return i === 0 ? "closed" : DETENTS[i - 1];
  }
  let best = "peek", d = Infinity;
  for (const [name, px] of order) { const dd = Math.abs(px - h); if (dd < d) { d = dd; best = name; } }
  return best;
}

// One step up or down from a detent (a swipe on the content, not a drag): "closed" below peek, full stays full.
export function step(from, dir) {
  const i = DETENTS.indexOf(from);
  if (dir > 0) return DETENTS[Math.min(DETENTS.length - 1, i + 1)];
  return i <= 0 ? "closed" : DETENTS[i - 1];
}

// The DOM controller. `frame`: the sheet (position: fixed, bottom-anchored). `grips`: the elements a drag may start
// on (the handle, the header). `onDetent(name)`: called at every rest ("closed" included — the caller closes the
// panel). `top`: the bar the sheet never covers. `vv`: the visual viewport (the keyboard shrinks it, and the sheet
// follows, so the line being typed stays above the keyboard).
export function attachSheet(frame, { grips, onDetent, top = 48, start = "half", vv = globalThis.visualViewport, win = globalThis } = {}) {
  // `top` may be a function: the top bar can grow a line mid-call (a Recording chip), and full is measured afresh each fit
  const topNow = () => (typeof top === "function" ? top() : top);
  let detent = start, heights = heightsFor(vv?.height || win.innerHeight, topNow());
  let drag = null;
  const apply = (name, animate = true) => {
    detent = name;
    frame.classList.toggle("is-animating", animate);
    frame.style.height = `${heights[name]}px`;
    frame.dataset.detent = name;
    onDetent?.(name);
  };
  const fit = () => {
    heights = heightsFor(vv?.height || win.innerHeight, topNow());
    // with the keyboard up the visual viewport is shorter: the sheet sits on its bottom edge, not the screen's
    const lift = vv ? Math.max(0, win.innerHeight - vv.height - vv.offsetTop) : 0;
    frame.style.bottom = `${lift}px`;
    if (!drag) frame.style.height = `${heights[detent]}px`;
  };
  const down = (e) => {
    if (e.button && e.button !== 0) return;
    if (e.target !== e.currentTarget && e.target.closest("button, a, input, select")) return; // the header's × is a button, not a grip
    drag = { y: e.clientY, h: frame.getBoundingClientRect().height, t: e.timeStamp, last: [e.clientY, e.timeStamp], from: detent, moved: false };
    frame.classList.remove("is-animating");
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not a pointer we can hold */ }
  };
  const move = (e) => {
    if (!drag) return;
    const dy = drag.y - e.clientY; // up is positive
    if (Math.abs(dy) > 4) drag.moved = true;
    const h = Math.max(40, Math.min(heights.full, drag.h + dy));
    frame.style.height = `${h}px`;
    drag.last = [e.clientY, e.timeStamp];
  };
  const up = (e) => {
    if (!drag) return;
    const d = drag; drag = null;
    if (!d.moved) { apply(d.from === "full" ? "half" : d.from === "half" ? "full" : "half"); return; } // a tap on the grip: the other size
    const dt = Math.max(1, e.timeStamp - d.last[1]);
    const vy = (d.last[0] - e.clientY) / dt; // px/ms, growing = positive
    const h = frame.getBoundingClientRect().height;
    apply(snapTo({ h, vy, from: d.from, heights }));
  };
  // a cancelled pointer (the browser took the touch) lets go and settles where the drag began
  const cancel = () => { if (!drag) return; const d = drag; drag = null; apply(d.from); };
  for (const g of grips) { g.addEventListener("pointerdown", down); g.addEventListener("pointermove", move); g.addEventListener("pointerup", up); g.addEventListener("pointercancel", cancel); }
  vv?.addEventListener("resize", fit);
  vv?.addEventListener("scroll", fit); // the keyboard's lift moves vv.offsetTop without a resize (iOS)
  win.addEventListener("resize", fit);
  fit();
  apply(start, false);
  return {
    get detent() { return detent; },
    set(name) { if (DETENTS.includes(name)) apply(name); },
    stepBy(dir) { const next = step(detent, dir); if (next === "closed") onDetent?.("closed"); else apply(next); },
    refit: fit,
    destroy() { for (const g of grips) { g.removeEventListener("pointerdown", down); g.removeEventListener("pointermove", move); g.removeEventListener("pointerup", up); g.removeEventListener("pointercancel", cancel); } vv?.removeEventListener("resize", fit); vv?.removeEventListener("scroll", fit); win.removeEventListener("resize", fit); },
  };
}
